// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { List, ListItem } from '@mui/material';
import { Table } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import type { NextAmortizationItem } from '@shared/advisory/aggregation/utils/build-next-amortizations';
import { type FC } from 'react';

export interface NextAmortizationsTimelineProps {
  readonly items: NextAmortizationItem[];
  readonly currency: string;
}

export const NextAmortizationsTimeline: FC<NextAmortizationsTimelineProps> = ({
  items,
  currency,
}) => {
  if (items.length === 0) {
    return (
      <p className="text-body font-normal text-primary-medium">
        <Translation i18nKey="common.no-data" ns="common" />
      </p>
    );
  }

  return (
    <div
      className="max-h-[400px] overflow-y-auto pr-1"
      data-scrollable
      data-testid="next-amortizations-timeline-scroll"
    >
      <List
        disablePadding
        className="relative flex flex-col gap-3"
        data-testid="next-amortizations-timeline"
      >
        <span
          aria-hidden
          className="absolute bottom-5 left-[4px] top-5 w-px bg-border-light"
        />

        {items.map(item => (
          <ListItem
            key={item.id}
            disablePadding
            className="relative flex items-center gap-3 md:gap-4"
          >
            <span
              aria-hidden
              className="relative z-10 size-2.5 shrink-0 rounded-full bg-secondary"
            />

            <div className="flex min-h-[4.5rem] flex-1 items-center justify-between gap-4 rounded-lg border border-border-light bg-page px-4 py-3.5 md:px-5 md:py-4">
              <div className="flex min-w-0 flex-col gap-1">
                <p className="text-body font-semibold leading-snug text-graph-text">
                  <Translation
                    i18nKey={
                      `aggregator.loans.${item.labelKey}` as 'aggregator.loans.amortization-monthly-installment'
                    }
                    ns="aggregator"
                    values={item.labelParams}
                  />
                </p>

                <p className="text-body font-normal text-primary-medium">
                  <Table.Data
                    format="date"
                    value={item.date}
                    formatOptions={{
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    }}
                    safeValue="-"
                    container={false}
                  />
                </p>
              </div>

              <p className="money-display shrink-0 text-lg font-semibold text-graph-text md:text-xl">
                <Table.Data
                  format="currency"
                  value={item.amount}
                  currency={currency}
                  container={false}
                />
              </p>
            </div>
          </ListItem>
        ))}
      </List>
    </div>
  );
};
